import { rgba } from 'polished';
import React, {useEffect, useRef, useState} from 'react';
import {Badge, Modal, OverlayTrigger, Tooltip} from 'react-bootstrap';
import styled from 'styled-components';
import { t } from 'ttag';
import useBasketContext from '../../../../../../contexts/basket/useBasketContext';
import useOperatorContext from '../../../../../../contexts/operator/useOperatorContext';
import useSiteContext from '../../../../../../contexts/site/useSiteContext';
import { Item, Modifier, ModifierGroup } from '../../../../../../contexts/site/site-context';
import { money } from '../../../../../../services/format';
import { estimateOffer, isOfferOnMenu, isOfferOpenAt, offerDetails, offerHeadline, offerNames, offersForItem, siteNow } from '../../../../../../services/offers';
import ItemUpdateButton from './components/ItemUpdateButton';
import ModifierCheckbox from './components/ModifierCheckbox';
import QuantityButton from './components/QuantityButton';
import { CTA } from '../../../../../general/Button';
import AllergensModal from './AllergensModal';
import {iLog} from "../../../../../../index";
import {Preferences} from "../../../MenuPage";
import {trackEvent} from "../../../../../../services/api/operator";

type ItemModalProps = {
    show: boolean;
    onHide: () => void;

    originalItem: Item | null;
    edit: boolean;

    viewOnly?: boolean;
    // what to say instead of that the location is closed, when it's something else that stops ordering
    viewOnlyMessage?: string;
    preferences: Preferences;
    scrollHeader?: boolean;
};

// edit: whether we're on the Menu page or the Basket area

const ItemModal = ({
    originalItem,
    show,
    onHide,
    edit,
    viewOnly = false,
    viewOnlyMessage,
    preferences
}: ItemModalProps) => {
    const [item, setItem] = useState<Item | null>(originalItem);
    const [quantity, setQuantity] = useState(item?.quantity ?? 1);
    //const [modifiersTotal, setModifiersTotal] = useState(item?.modifiersTotal ?? 0);
    // const [selectedModifiers, setSelectedModifiers] = useState<Modifier[]>([]);
    const [valid, setValid] = useState(false);
    const [showValidationIssues, setShowValidationIssues] = useState(false);
    const [validationIssues, setValidationIssues] = useState<any>([]);
    const [itemTotal, setItemTotal] = useState(item?.price ?? 0);

    const operatorContext = useOperatorContext();
    const basketContext = useBasketContext();
    const siteContext = useSiteContext();

    const itemContent = useRef<HTMLDivElement>(null);
    const itemImage = useRef<HTMLDivElement>(null);

    const [showAllergens, setShowAllergens] = useState(false);
    const [showHeader, setShowHeader] = useState(false);
    const [addingItemTransition, setAddingItemTransition] = useState(false);

    // This is solely here to set the defaults and to set the group_uid which
    // we use for efficient mod selections AND to reset on unmount
    useEffect(() => {
        if (!edit && originalItem) {

            originalItem.modifier_groups = originalItem.modifier_groups.map((group) => {
                group.modifiers = group.modifiers.map((mod) => {
                    mod.quantity = mod.is_default ? 1 : 0;
                    mod.group_uid = group.uid;
                    return mod;
                });

                return group;
            });

        }

        setItem(originalItem);
        setQuantity(originalItem?.quantity ?? 1);

        return () => {
            setItem(null);
            setQuantity(1);
            setItemTotal(0);
        };
    }, [ edit, originalItem]);

    // Validate the item has modifiers that match the requirements (maybe do price in here as well???)
    useEffect(() => {
        if (!item) {
            return;
        }

        let validItem = true;
        let validItemIssues: any[] = [];

        item.modifiersTotal = 0; //reset

        /* If all modifier group min requirements are met then un-disable the button */
        item.modifier_groups.forEach((group) => {
            let groupModsSelected = 0;

            group.modifiers.forEach((mod) => {
                if (mod.quantity > 0) {
                    // selectedMods.push(mod);

                    groupModsSelected += mod.quantity;
                    item.modifiersTotal += mod.price * (mod.quantity ?? 1);
                }
            });

            if (groupModsSelected < group.min) {
                validItem = false;
                validItemIssues.push(group.uid);
            }
        });


        iLog('ItemModal.item', item);

        setItemTotal((item.price + item.modifiersTotal) * quantity);
        setValid(validItem);
        setValidationIssues(validItemIssues);
        setItem(item);

        return () => {
            setValid(false);
            setValidationIssues([]);
        }
    }, [quantity, item]);


    useEffect(() => {

        function updateScrollPosition() {

            if (itemContent && itemContent.current ) {

                let offset = 32;

                if (itemImage && itemImage.current) {
                    offset += itemImage.current.clientHeight
                }

                if(itemContent?.current?.scrollTop > offset) {
                    setShowHeader(true)
                } else {
                    setShowHeader(false)
                }
            }

        }

        updateScrollPosition();

        if (itemContent && itemContent.current) {
            itemContent.current.addEventListener("scroll", updateScrollPosition, false);

            document.getElementById('item-modal')?.classList.add('anim-enter');

            return () => {
                setShowHeader(false);
                itemContent?.current?.removeEventListener("scroll", updateScrollPosition, false);

                document.getElementById('item-modal')?.classList.remove('anim-enter');
            };
        }

    }, [itemContent.current, show]);

    if (!item || item.is_hidden) {
        return null;
    }

    const renderModifierGroups = (groups: ModifierGroup[]) => {
        if (groups.length <= 0) {
            return null;
        }

        return groups.map((group, index) => (
            <ModifierGroupSeparator hidden={group.hidden}>
                <ModifierGroupWrapper className="mod-group" id={`group-${group.uid}`} key={`group-${group.uid}-${index}`}>
                    <div className="mod-group__header">
                        <h3 className="mod-group__header__title">
                            {group.name}
                        </h3>
                        <div className={`mod-group__header__subtitle ${validationIssues.includes(group.uid) && showValidationIssues ? 'group-is-invalid' : ''}`}>
                            {group.min > 0 ? (
                                <span className="required">
                                    {t`Required`}
                                </span>
                            ) : (
                                <span className="optional">
                                    {t`Optional`}
                                </span>
                            )}
                            {group.modifiers.length > 1 && (
                                <>
                                    &nbsp;&middot; {group.subtitle}
                                </>
                            )}
                        </div>

                        { validationIssues.includes(group.uid) && showValidationIssues && (
                            <span className="mod-group__required-alert">
                                <span className="material-symbols-outlined">info</span>
                                This choice is required
                            </span>
                        )}

                    </div>
                    <div className="mod-wrap">
                        {renderModifierItems(group.modifiers, modifierDisplayType(group), getMaxLimit(group))}
                    </div>
                </ModifierGroupWrapper>
            </ModifierGroupSeparator>
        ));
    };

    const modifierDisplayType = (group: ModifierGroup) => {
        if (group.multi_choice) {
            if (group.max_per_item === 1) {
                return 'checkbox';
            } else {
                return 'quantity';
            }
        } else {
            return 'radio';
        }
    }

    const renderModifierItems = (modifierItems: Modifier[], modifierType: 'quantity'|'checkbox'|'radio', maxReached: boolean) => {
        return modifierItems.map((modifierItem, index) => {
            return (
                <ModifierCheckbox
                    key={`mItem-${modifierItem.uid}-${index}`}
                    originalModifier={modifierItem}
                    type={modifierType}
                    selected={modifierItem.quantity > 0}
                    lastCheckbox={(modifierItems.length - 1) === index}
                    maxReached={maxReached}
                    updateModifier={updateModifier}
                    preferences={preferences}
                />
            );
        });
    };

    const updateModifier = (modifier: Modifier, quantity: number) => {
        // Map updated mod into selectedItem
        let newItem: Item = { ...item };

        iLog('update mod', [modifier.name, quantity]);

        newItem.modifier_groups = newItem.modifier_groups.map((group, group_idx) => {

            if (group.uid !== modifier.group_uid) {
                return group;
            }

            let multi = group.max > 1;

            group.modifiers = group.modifiers.map((mod, idx) => {

                // unselect any previously selected radio
                if ( !multi && quantity > 0 ) {
                    mod.quantity = 0;
                }

                if (mod.uid === modifier.uid) {
                    mod.quantity = quantity;
                }
                return mod;
            });

            return group;
        });

        // which mod group is next?
        // the one which is required and not completed
        let next = newItem.modifier_groups.find((mg) => {
            let selected_mods = mg.modifiers.filter(m => m.quantity > 0 ).length;

            if ( selected_mods === mg.max ) {
                return false;
            }
            return true;
        });

        setItem(newItem);
    };

    const hideItemModal = () => {
        setShowValidationIssues(false);
        onHide();
    };

    const handleItemUpdateButton = () => {
        iLog('adding item to basket & reset', item);
        trackEvent('add-item', siteContext.site?.uid ?? '')

        // perform some validation!
        if ( !valid ) {

            setShowValidationIssues(true);

            let uid = validationIssues[0];

            let target = document.querySelector('#group-' + uid) as HTMLElement | null;
            let element = document.querySelector('.modal-body') as HTMLElement | null;


            const yOffset = -30;
            if ( target && element ) {
                element.scrollTo({ top: target.offsetTop + yOffset, behavior: 'smooth' });
            }

            return false;
        }

        setAddingItemTransition(true);

        // reset the selected modifiers array to empty to clean up
        item.modifiers = [];

        basketContext.addItem(item, quantity, edit);

        setTimeout(() => {
            setAddingItemTransition(false);
            hideItemModal();
        }, 500);

    };

    // What an offer takes off the item as it is on the button: a percentage off the item itself and what is added to it. Whether
    // an offer on the basket as a whole applies is for the basket to say, so the price is what the item costs on its own.
    const itemOfferSaving = item
        ? estimateOffer(
            siteContext.offers,
            siteContext.menu,
            [{ uid: item.uid, price: item.price, quantity, modifiersTotal: item.modifiersTotal }],
            0,
            siteNow(siteContext.site?.timezone),
            ['item_discount']
        )?.discount ?? 0
        : 0;

    // What offers say about the item, and their small print: a line for each that is on the item now.
    const renderOffers = () => {
        const format = (pence: number) => money(pence, operatorContext.operator?.ccy, operatorContext.operator?.locale);
        const now = siteNow(siteContext.site?.timezone);
        const offers = offersForItem(siteContext.offers, item.uid, siteContext.menu).filter((offer) => isOfferOnMenu(offer, siteContext.menu) && isOfferOpenAt(offer, now));

        if (offers.length === 0) {
            return null;
        }

        return (
            <div className="item__details__offers">
                {offers.map((offer) => (
                    <div key={offer.uid} className="item__details__offer">
                        <span className="item__details__offer-headline">{offerHeadline(offer, offerNames(offer, siteContext.menu), format)}</span>
                        {offerDetails(offer, format).map((detail) => (
                            <span key={detail} className="item__details__offer-detail">{detail}</span>
                        ))}
                    </div>
                ))}
            </div>
        );
    };

    const renderTitleBadges = () => {
        let badges = [];
        if (item.is_new) {
            badges.push(
                <Badge key={`tfbg-${item.uid}-new`} className="badge--title badge--title--new">
                    <div className="badge--title__icon material-symbols-outlined material-symbols-outlined--filled">
                        kid_star
                    </div>
                    <span className={'badge--title__text'}>
                        {t`New`}
                    </span>
                </Badge>,
            );
        }
        if (item.is_popular) {
            badges.push(
                <Badge key={`tfbg-${item.uid}-popular`} className="badge--title badge--title--popular">
                    <div className="badge--title__icon material-symbols-outlined material-symbols-outlined--filled">
                        kid_star
                    </div>
                    <span className={'badge--title__text'}>
                        {t`Popular`}
                    </span>
                </Badge>,
            );
        }
        if (item.is_offer) {
            badges.push(
                <Badge key={`tfbg-${item.uid}-offer`} className="badge--title badge--title--offer">
                    <div className="badge--title__icon material-symbols-outlined material-symbols-outlined--filled">
                        kid_star
                    </div>
                    <span className={'badge--title__text'}>
                        {t`Meal deal`}
                    </span>
                </Badge>,
            );
        }
        if (badges.length > 0) {
            return (
                <div className="badges-title">
                    {badges.map((badge) => badge)}
                </div>
            )
        } else {
            return null
        }
    };


    const renderDietBadges = () => {
        let badges = [];
        if (item.is_vegan) {
            badges.push(
                <OverlayTrigger
                    key={'veganPlacement'}
                    placement={'top'}
                    overlay={
                        <Tooltip id={`tooltip-veganPlacement`}>
                            Suitable for Vegans
                        </Tooltip>
                    }
                >
                    <Badge className="badge--item-modal badge--item-modal--circular" key={`tfbg-${item.uid}-vegan`} title="Vegan">
                        Vg
                    </Badge>
                </OverlayTrigger>

            );
        }

        if (item.is_vegetarian) {
            badges.push(
                <OverlayTrigger
                    key={'vegetarianPlacement'}
                    placement={'top'}
                    overlay={
                        <Tooltip id={`tooltip-vegetarianPlacement1`}>
                            Suitable for Vegetarians
                        </Tooltip>
                    }
                >
                    <Badge className="badge--item-modal badge--item-modal--circular" key={`tfbg-${item.uid}-vegetarian`} title="Vegetarian">
                        V
                    </Badge>
                </OverlayTrigger>
            );
        }

        if (item.is_gluten_free) {
            badges.push(
                <OverlayTrigger
                    key={'gfPlacement'}
                    placement={'top'}
                    overlay={
                        <Tooltip id={`tooltip-gfPlacement`}>
                            Gluten-Free
                        </Tooltip>
                    }
                >
                    <Badge className="badge--item-modal badge--item-modal--circular" key={`tfbg-${item.uid}-gf`} title="Gluten-Free">
                        Gf
                    </Badge>
                </OverlayTrigger>
            );
        }

        if (item.is_halal) {
            badges.push(
                <OverlayTrigger
                    key={'halalPlacement'}
                    placement={'top'}
                    overlay={
                        <Tooltip id={`tooltip-halalPlacement`}>
                            Halal
                        </Tooltip>
                    }
                >
                    <Badge className="badge--item-modal badge--item-modal--circular" key={`tfbg-${item.uid}-halal`} title="Halal">
                        H
                    </Badge>
                </OverlayTrigger>
            );
        }

        if (item.is_kosher) {
            badges.push(
                <OverlayTrigger
                    key={'kosherPlacement'}
                    placement={'top'}
                    overlay={
                        <Tooltip id={`tooltip-kosherPlacement`}>
                            Kosher
                        </Tooltip>
                    }
                >
                    <Badge className="badge--item-modal badge--item-modal--circular" key={`tfbg-${item.uid}-kosher`} title="Kosher">
                        K
                    </Badge>
                </OverlayTrigger>
            );
        }

        if (badges.length > 0) {
            return <>{badges.map((badge) => badge)}</>;
        }
        return null;
    };

    return (
        <ItemModalWrapper
            show={show}
            onHide={onHide}
            size="lg"
            backdrop={true}
            backdropClassName="modal-item-backdrop"
            className="item-modal-dialog"
            centered
            animation={false}
            id={'item-modal'}
            image={item.image}
        >
            <Modal.Header className={`modal-show-header-shadow ${showHeader ? 'modal-show-header' : ''}`}>
                <h2 className='modal__title'>{item.name}</h2>
                <div className="modal-close__wrapper">
                    <button role="button" className={'modal-close__btn'} onClick={() => hideItemModal()}>
                        <span className="material-symbols-outlined">close</span>
                    </button>
                </div>
            </Modal.Header>
            <Modal.Body ref={itemContent}>
                {item.image ? (
                    <ItemImage ref={itemImage} image={item.image} />
                ) : null}
                <div className="item__details">
                    <h2 className="item__details__title">
                        {item.name}
                    </h2>

                    { renderDietBadges() || (preferences.calories && item.calories && item.calories.length > 0) ? (
                        <div className="item__details__calories">
                            { preferences.calories && item.calories && item.calories.length > 0 ? (
                                <span>{item.calories} kcal</span>
                            ) : null }
                            <span className="item__details__badges-diet">{renderDietBadges()}</span>
                        </div>
                    ) : null }

                    { item.description && (
                        <div className="item__details__description">{item.description}</div>
                    )}

                    { renderTitleBadges() }

                    { renderOffers() }

                    <div className="item__allergens">
                        <AllergensModal
                            show={showAllergens}
                            allergens={item.allergens}
                            onHide={() => setShowAllergens(false)}
                        />
                        <button className={"item__allergens__btn"} onClick={() => setShowAllergens(true)}><span className="material-symbols-outlined">info</span> <span className="item__allergens__btn__text">Allergen info</span></button>
                    </div>
                </div>
                <div className="item__modifiers">
                    { renderModifierGroups(item.modifier_groups) }
                </div>
            </Modal.Body>
            <Modal.Footer className="modal-show-footer-shadow">
                <div className="footerContent">
                    { viewOnly ? (
                        <>
                            <LocationClosed>
                                { viewOnlyMessage ? (
                                    <p>{viewOnlyMessage}</p>
                                ) : (
                                    <p>This location is not currently accepting orders. Please check back later to place an order or <a href="/">choose another location</a>.</p>
                                )}
                            </LocationClosed>
                            <LocationClosedButton onClick={hideItemModal}>
                                <span>Close</span>
                            </LocationClosedButton>
                        </>
                    ) : (
                        <>
                            <QuantityButton minimum={true} quantity={quantity} setQuantity={setQuantity} type={'large'} />
                            <ItemUpdateButton
                                validated={valid}
                                edit={edit}
                                total={itemTotal - itemOfferSaving}
                                loading={addingItemTransition}
                                onClick={handleItemUpdateButton}
                            />
                        </>
                    )}
                </div>
            </Modal.Footer>
        </ItemModalWrapper>
    );
};

const LocationClosed = styled.div`
    p {
        color: var(--text-black);
        font-weight: 400;
        font-size: 16px;
        line-height: 24px;
        margin: 0;
    }
`;

const LocationClosedButton = styled(CTA)`
    height: 48px;

    @media screen and (min-width: 768px) {
        width: 528px;
    }
`;


const getMaxLimit = (group: ModifierGroup) => {
    let maxLimitReached = false;
    let checked = 0;
    group.modifiers.forEach((mod) => {
        if (mod.quantity > 0) {
            checked = checked + (mod.quantity ?? 1);
            if (checked >= group.max) {
                maxLimitReached = true;
            }
        }
    });

    return maxLimitReached;
};

const ItemModalWrapper = styled(Modal)<{image: boolean}>`
    .modal-dialog .modal-content {
        @media screen and (min-width: 768px) {
            width: 560px;
            max-width: none;
        }
    }

    .modal-content {
        @media screen and (max-width: 767px) {
            height: 100% !important;
        }
    }

    .modal-header {
        display: flex;
        align-items: center;
        height: 56px;
        background-color: transparent;
        z-index: 1;
        width: 100%;
        padding: 16px;

        > div { width: 100% }

        .modal__title {
            margin-bottom: 0;
            transition: opacity 0.2s ease-in-out;
            opacity: 0;
            overflow: hidden;
            font-weight: bold;
            text-overflow: ellipsis;
            white-space: nowrap;
            padding-right: 40px;
            font-family: var(--text_body_font);
            letter-spacing: var(--text_body_font_spacing);
            text-transform: var(--text_body_font_case);
        }

        &.modal-show-header .modal__title {
            opacity: 1;
        }
    }

    .modal-body {
        padding: 0;
        padding-bottom: 10px;
        overflow-y: auto;
    }

    .item {
        &__details {
            padding: 16px;
            ${(props) => props.image ? 'padding-top: 0;' : ''}
            &__title {
                margin-bottom: 8px;
                font-family: var(--text_body_font);
                letter-spacing: var(--text_body_font_spacing);
                text-transform: var(--text_body_font_case);
            }
            &__description {
                color: var(--text-grey);
                margin-bottom: 8px;
                display: inline-block;
            }
            &__offers {
                display: flex;
                flex-direction: column;
                gap: 8px;
                margin: 8px 0;
            }
            &__offer {
                display: flex;
                flex-direction: column;
                font-size: 14px;
                line-height: 20px;
            }
            &__offer-headline {
                color: var(--text-black);
                font-weight: 500;
            }
            &__offer-detail {
                color: var(--text-grey);
            }
            &__badges-diet {
                display: flex;
                gap: 8px;
                align-items: center;
            }
            &__calories {
                font-size: 14px;
                line-height: 20px;
                margin-bottom: 8px;
                color: var(--text-grey);
                display: flex;
                align-items: baseline;
                gap: 8px;
                flex-shrink: 0;
                .bullet {
                    width: 4px;
                    height: 4px;
                    background-color: var(--text-grey);
                    border-radius: 50%;
                    display: inline-block;
                    margin: 0 5px;
                }

                i {
                    margin-left: 8px;
                    height: 12px;
                    width: 12px;
                    margin-top: 2px;
                }
            }

        }


        &__modifiers {
            .mod-group {
                > p {
                    padding: 0px 0px;
                }
                .mod-wrap > div {
                    display: flex;
                    align-items: center;

                    label {
                        padding-left: 0;
                        min-width: 0;
                    }

                    &:first-child { border-top: none !important; }
                }

                .mod-group__required-alert {
                    display: flex;
                    align-items: center;
                    border: none;
                    color: var(--invalid);
                    padding-bottom: 16px;
                    margin: 0;

                    span {
                        margin-right: 4px;
                    }
                }
            }
        }

        &__allergens {
            line-height: 0;
            &__btn {
                font-size: 16px;
                line-height: 24px;
                background-color: transparent;
                border: 0;
                font-weight: 500;
                color: var(--color_ui_5);
                padding: 0;
                position: relative;
                display: inline-flex;
                align-items: center;
                gap: 4px;

                &__text {
                    text-decoration: underline;
                    text-underline-offset: 2px;
                }

                & .material-symbols-outlined {
                    font-size: 24px;
                    line-height: 24px;
                }
            }
        }
    }

    .modal-footer {
        padding: 16px !important;
        transition: box-shadow 0.15s ease-in-out;

        .footerContent {
            width: 100%;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            margin: 0 auto;
            gap: 16px;
        }
    }

    .badges-title {
        margin-bottom: 8px;
        line-height: 0;
    }
`;

const ModifierGroupSeparator = styled.div<{ hidden: boolean }>`
    ${(props) => props.hidden ? 'display: none;' : ''}
    border-top: 1px solid var(--border-grey);
`;

const ModifierGroupWrapper = styled.div`
    margin: 0 16px 16px 16px;
    font-size: 14px;

    .mod-group__header {
        &__title {
            width: 100%;
            display: inline-block;
            white-space: nowrap;
            font-weight: bold;
            text-overflow: ellipsis;
            font-family: var(--text_body_font);
            letter-spacing: var(--text_body_font_spacing);
            text-transform: var(--text_body_font_case);
            overflow: hidden;
            padding-top: 16px;
            margin-bottom: -4px;
            color: var(--text_subsubheading_color);
        }

        &__subtitle {
            font-size: 14px;
            font-weight: 400;
            line-height: 20px;
            padding-bottom: 16px;
            color: var(--text-grey);
            .required, .optional {
                font-weight: bold;
            }
            &.group-is-invalid {
                padding-bottom: 0;
            }
        }
    }
`;

const ItemImage = styled.div<{ image: string }>`
    margin: 16px;
    max-width: 735px;
    height: calc(100vw * 0.5625); // ratio 16:9
    background: no-repeat center center;
    background-size: cover;
    background-image: url('${(props) => props.image}');
    background-position: center;
    background-size: cover;
    border-radius: var(--radius_xl);

    @media screen and (min-width: 768px) {
        height: calc(560px * 0.5625);
    }
`;

const Allergens = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin-top: 12px;

    span {
        background: #E7E7E7;
        padding: 0 6px;
        border-radius: 4px;
        font-size: 12px;
        font-weight: 500;
    }
`;

const Separator = styled.div`
    margin: 20px -24px;
    height: 20px;
    border-top: 8px solid #f7f7f7;
    @media screen and (max-width: 575px) {
        margin: 24px -16px;
    }
`;

export default ItemModal;
